import { Plan } from '@/types'
import { env } from '@/lib/runtimeEnv'

export const paidFeatures = [
  { name: 'Unlimited Resume Scans' },
  {
    name: 'Unlimited One-Click AI Optimizations',
    modal: 'OneClickOptimizerUpgradeModal'
  },
  { name: 'Unlimited keyword comparisons' },
  { name: 'Unlimited ATS & Recruiter Findings' },
  { name: 'AI Cover Letter Generator' },
  { name: 'AI Bullet Point Generator' },
  { name: 'AI Keyword Synonym Detector' },
  { name: 'Linkedin Optimizer' },
  { name: 'AI Networking Message Generator' },
  { name: 'Job Tracker' },
  { name: 'Post Interview Thank You Notes' },
  { name: 'Job Board' },
  { name: 'Chrome Extension' },
  { name: 'Resume Builder' },
  { name: 'Resume Manager' },
  { name: 'ATS Revealed eBook' },
  { name: 'ATS Friendly Resume Templates' },
  { name: 'Premium Cover Letter Template' },
  { name: 'Jobscan Learning Center' },
]

export const trialFeatures = [
  { name: 'Unlimited Resume Scans' },
  {
    name: 'Unlimited One-Click AI Optimizations',
    modal: 'OneClickOptimizerUpgradeModal'
  },
  { name: 'Unlimited keyword comparisons' },
  { name: 'Unlimited ATS & Recruiter Findings' },
  { name: 'AI Cover Letter Generator' },
  { name: 'AI Bullet Point Generator' },
  { name: 'AI Keyword Synonym Detector' },
  { name: 'Linkedin Optimizer (after trial)' },
  { name: 'AI Networking Message Generator (after trial)' },
  { name: 'Job Tracker' },
  { name: 'Post Interview Thank You Notes' },
  { name: 'Job Board' },
  { name: 'Chrome Extension' },
  { name: 'Resume Builder' },
  { name: 'Resume Manager' },
  { name: 'ATS Revealed eBook' },
  { name: 'ATS Friendly Resume Templates' },
  { name: 'Premium Cover Letter Template' },
  { name: 'Jobscan Learning Center' },
]

const freeFeatures = [
  { name: '5 Free Resume Scans on signup' },
  { name: '5 Free Resume Scans monthly' },
  { name: '5 ATS & Recruiter Findings' },
  { name: 'Job Tracker' },
  { name: 'Post Interview Thank You Notes' },
  { name: 'Job Board' },
  { name: 'Resume Builder' },
  { name: 'Resume Manager' },
  { name: 'Chrome Extension' },
  { name: 'Jobscan Learning Center' },
]

const planFeatures = [
  {
    planName: 'Free',
    isConsumerPlan: true,
    isPromoted: false,
    features: freeFeatures,
  },
  {
    isConsumerPlan: true,
    isPromoted: true,
    features: paidFeatures,
  },
  {
    isConsumerPlan: true,
    isPromoted: false,
    features: paidFeatures,
  },
]

const transformer = (promoPlans: Plan[], stripeDiscount: number) => {
  return promoPlans.map((promoPlan, index): Plan => {
    const plan = Object.assign(planFeatures[index], promoPlan)

    if (plan.hasTrial) plan.features = trialFeatures
    if (env.VITE_ENABLE_NETWORKING_MESSAGE_GENERATOR !== 'true') {
      plan.features = plan.features.filter(feature => !feature.name.includes('AI Networking Message Generator'))
    }
    plan.stripeDiscount = stripeDiscount
    plan.basePrice = calculateBasePrice(plan)
    plan.promoDisplay = createPromoDisplay(plan)
    plan.displayName = generatePlanName(plan)
    plan.tagline = createTagline(plan)
    plan.trialDays = plan.hasTrial ? plan.trialDays || 30 : 0
    plan.trialDurationText = generateTrialDurationText(plan)
    plan.billValue = createBillValueValue(plan)

    return plan
  })
}

export const createBillValueValue = (plan: Plan): string => {
  return `$${plan.price} every
    ${plan.duration > 1 ? `${plan.duration} months` : 'month'}`
}

export const calculateBasePrice = (plan: Plan) => {
  if (plan.price === 0) return 0

  return (plan.price * 100) / (100 - plan.stripeDiscount)
}

export const generatePlanName = (plan: Plan): string => {
  if (plan.price === 0) return 'Free'

  switch (plan.duration) {
    case 1:
      return 'Monthly'
    case 3:
      return 'Quarterly'
    case 12:
      return 'Annual'
    case 6:
      return 'Biannually'
    default:
      return 'Plan'
  }
}

export const createTagline = (plan: Plan): string => {
  const length = plan.duration > 1 ? `${plan.duration} ` : ''
  const pluralChar = `${plan.duration > 1 ? 's' : ''}`

  const periodQualifier = `per ${length}month${pluralChar}`
  const monthlyValue = Math.ceil((plan.price / plan.duration) * 100) / 100

  if (plan.hasTrial && plan.trialDays) {
    //Javascript should never have to calculate.
    return `<span>
              <span class="line-through	">$49.95</span>
              <span class="font-semibold">$${monthlyValue}</span>
              per month
            </span>`
  }

  if (plan.planName === 'free' || plan.price === 0) {
    return `$${plan.price} forever`
  }

  return `<span>
            <span class="font-semibold">$${monthlyValue}</span>
            per month
          </span>`
}

export const createPromoDisplay = (plan: Plan): string => {
  if (plan.price === 0) return ''

  if (plan.stripeDiscount) {
    return `Promo: ${plan.stripeDiscount}% off`
  }

  if (plan.planName === 'quarter90wtrial') {
    return `Save 40%`
  }

  return ''
}

export const generateTrialDurationText = (plan: Plan): string => {
  const { trialDays } = plan

  switch (trialDays) {
    case 30:
    case 31:
      return '1 month'
    case 7:
      return '1 week'
    case 14:
      return '2 weeks'
    default:
      return `${trialDays} day${trialDays > 1 ? 's' : ''}`
  }
}

export default transformer
